// src/lib/legal-content.ts
import { COMPANY } from './company-info';

// ============================================
// TYPE DEFINITIONS
// ============================================

interface LegalSection {
  title: string;
  content: string;
}

export interface LegalDocument {
  title: string;
  subtitle: string;
  lastUpdated: string;
  sections: LegalSection[];
}

// ============================================
// LANGUAGE SUPPORT WITH TYPE GUARDS
// ============================================

const SUPPORTED_LANGUAGES = ['pl', 'en', 'uk'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Type guard for validating language parameter
 * Use this instead of unsafe type casting
 */
function isSupportedLanguage(lang: string | null): lang is SupportedLanguage {
  return lang !== null && SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
}

/**
 * Safely get language from query param with fallback
 */
function getSafeLanguage(lang: string | null): SupportedLanguage {
  return isSupportedLanguage(lang) ? lang : 'pl';
}

// ============================================
// LEGAL DOCUMENTS (Type-indexed for safety)
// ============================================

export type MultilingualLegalDocument = Record<SupportedLanguage, LegalDocument>;

export const privacyPolicy: MultilingualLegalDocument = {
  pl: {
    title: 'Polityka Prywatności',
    subtitle: 'Jak przetwarzamy Twoje dane',
    lastUpdated: '12 marca 2026',
    sections: [
      {
        title: '1. Administrator danych',
        content: `Administratorem Twoich danych osobowych jest ${COMPANY.name}, ${COMPANY.address}, NIP: ${COMPANY.nip}, REGON: ${COMPANY.regon}.

Kontakt: ${COMPANY.email}`,
      },
      {
        title: '2. Jakie dane zbieramy',
        content: `Zbieramy następujące kategorie danych:

• Dane identyfikacyjne (imię, nazwisko, NIP)
• Dane kontaktowe (adres e-mail)
• Dane finansowe (przychody, koszty - wyłącznie w celu kalkulacji podatkowych)
• Dane techniczne (adres IP, typ przeglądarki, logi dostępu)

Wszystkie dane finansowe są przetwarzane wyłącznie lokalnie w Twojej przeglądarce lub bezpiecznie szyfrowane.`,
      },
      {
        title: '3. Cele przetwarzania',
        content: `Twoje dane przetwarzamy w celu:

• Świadczenia usługi kalkulacji podatkowych (art. 6 ust. 1 lit. b RODO)
• Komunikacji z Tobą (art. 6 ust. 1 lit. b RODO)
• Wypełnienia obowiązków prawnych (art. 6 ust. 1 lit. c RODO)
• Ulepszania naszych usług (art. 6 ust. 1 lit. f RODO)`,
      },
      {
        title: '4. Okres przechowywania',
        content: `Dane przechowujemy:

• Dane konta - przez okres korzystania z usługi
• Dane finansowe - przez okres wymagany przepisami podatkowymi (5 lat)
• Logi techniczne - maksymalnie 12 miesięcy
• Dane analityczne i nagrania sesji (PostHog, po wyrażeniu zgody) - planowany maksymalnie 12-miesięczny okres retencji po wdrożeniu ustawienia w PostHog`,
      },
      {
        title: '5. Twoje prawa',
        content: `Przysługuje Ci prawo do:

• Dostępu do swoich danych
• Sprostowania danych
• Usunięcia danych ("prawo do bycia zapomnianym")
• Ograniczenia przetwarzania
• Przenoszenia danych
• Sprzeciwu wobec przetwarzania
• Cofnięcia zgody w dowolnym momencie

Aby skorzystać z tych praw, skontaktuj się z nami: ${COMPANY.email}

Wnioski o usunięcie danych kierujemy również do naszych podmiotów przetwarzających, jeśli przechowują identyfikatory lub dane pomocnicze powiązane z Twoim kontem.`,
      },
      {
        title: '6. Podmioty przetwarzające i transfery danych',
        content: `Korzystamy z dostawców, którzy przetwarzają dane w naszym imieniu wyłącznie w zakresie niezbędnym do świadczenia usługi:

• PostHog Inc. - analityka produktu i nagrania sesji po wyrażeniu zgody; dane dla EU Cloud są hostowane w regionie EU (Frankfurt / Niemcy); standardowa DPA: https://posthog.com/dpa; lista subprocesorów: https://posthog.com/subprocessors
• Vercel Inc. - hosting i dostarczanie aplikacji
• Supabase Inc. - infrastruktura bazodanowa, uwierzytelnianie użytkowników i przechowywanie danych konta
• Resend, Inc. - wysyłka wiadomości transakcyjnych i obsługa kontaktu
• Stripe, Inc. - obsługa płatności, jeśli korzystasz z płatnych funkcji

Udostępniamy wyłącznie dane niezbędne do wykonania danej usługi.`,
      },
      {
        title: '7. Cookies, zgoda i nagrania sesji',
        content: `Na stronach publicznych zapisujemy cookie zgody \`bookeper_consent\`, aby zapamiętać decyzję dotyczącą analityki. W aplikacji zapisujemy dodatkowo wpis \`bookeper_cookie_consent\` w pamięci przeglądarki, aby odtworzyć wybór użytkownika po zalogowaniu.

Po wyrażeniu zgody w aplikacji Bookeper włączamy analitykę produktu PostHog oraz first-party cookies i wpisy localStorage potrzebne do przypisania zdarzeń do sesji użytkownika i mierzenia korzystania z funkcji.

Nagrania sesji w PostHog służą wyłącznie do analizy jakości produktu, błędów i ścieżek użytkownika. Pola formularzy oraz wskazane obszary interfejsu są maskowane, a wzorce danych identyfikujących usuwamy przed wysłaniem danych do narzędzia analitycznego.

Zgodę możesz wycofać w dowolnym momencie przez ustawienia przeglądarki. W aplikacji udostępniamy także bezpośredni przycisk do ponownego otwarcia ustawień cookies na stronie polityki prywatności.`,
      },
      {
        title: '8. Bezpieczeństwo danych',
        content: `Stosujemy odpowiednie środki techniczne i organizacyjne:

• Szyfrowanie danych (TLS/SSL)
• Regularne kopie zapasowe
• Kontrola dostępu
• Monitorowanie bezpieczeństwa

Nasze kalkulatory działają deterministycznie - nie używamy AI do przetwarzania Twoich danych finansowych.`,
      },
    ],
  },
  en: {
    title: 'Privacy Policy',
    subtitle: 'How we process your data',
    lastUpdated: 'March 12, 2026',
    sections: [
      {
        title: '1. Data Controller',
        content: `The controller of your personal data is ${COMPANY.name}, ${COMPANY.address}, NIP: ${COMPANY.nip}, REGON: ${COMPANY.regon}.

Contact: ${COMPANY.email}`,
      },
      {
        title: '2. Data We Collect',
        content: `We collect the following categories of data:

• Identification data (name, surname, tax ID)
• Contact data (email address)
• Financial data (income, expenses - solely for tax calculations)
• Technical data (IP address, browser type, access logs)

All financial data is processed locally in your browser or securely encrypted.`,
      },
      {
        title: '3. Processing Purposes',
        content: `We process your data for:

• Providing tax calculation services (Art. 6(1)(b) GDPR)
• Communication with you (Art. 6(1)(b) GDPR)
• Legal obligations (Art. 6(1)(c) GDPR)
• Improving our services (Art. 6(1)(f) GDPR)`,
      },
      {
        title: '4. Retention Period',
        content: `We retain data for:

• Account data - duration of service use
• Financial data - period required by tax law (5 years)
• Technical logs - maximum 12 months
• Analytics data and session recordings (PostHog, after consent) - planned maximum 12-month retention once the PostHog setting is applied`,
      },
      {
        title: '5. Your Rights',
        content: `You have the right to:

• Access your data
• Rectify data
• Erase data ("right to be forgotten")
• Restrict processing
• Data portability
• Object to processing
• Withdraw consent at any time

To exercise these rights, contact us: ${COMPANY.email}

We also relay erasure requests to our processors when they store identifiers or supporting records associated with your account.`,
      },
      {
        title: '6. Processors and data transfers',
        content: `We use vendors that process data on our behalf only to the extent necessary to deliver the service:

• PostHog Inc. - product analytics and session recordings after consent; EU Cloud data is hosted in the EU region (Frankfurt / Germany); standard DPA: https://posthog.com/dpa; subprocessors list: https://posthog.com/subprocessors
• Vercel Inc. - application hosting and delivery
• Supabase Inc. - database infrastructure, user authentication, and account-data storage
• Resend, Inc. - transactional email delivery and support communication
• Stripe, Inc. - payment processing if you use paid features

We share only the data necessary for the relevant service.`,
      },
      {
        title: '7. Cookies, consent, and session recordings',
        content: `On the public site we store the \`bookeper_consent\` consent cookie to remember your analytics choice. Inside the application we also store \`bookeper_cookie_consent\` in browser storage so the user's choice can be restored after sign-in.

Once consent is granted in the Bookeper application, we enable PostHog product analytics together with first-party cookies and localStorage entries needed to associate events with a user session and measure feature usage.

PostHog session recordings are used only for product-quality analysis, debugging, and understanding user journeys. Form fields and selected interface regions are masked, and we redact identifying data patterns before data is sent to the analytics tool.

You can withdraw consent at any time through your browser settings. Inside the application we also expose a direct button on the privacy-policy page that reopens cookie settings.`,
      },
      {
        title: '8. Data Security',
        content: `We implement appropriate technical and organizational measures:

• Data encryption (TLS/SSL)
• Regular backups
• Access control
• Security monitoring

Our calculators work deterministically - we do not use AI to process your financial data.`,
      },
    ],
  },
  uk: {
    title: 'Політика конфіденційності',
    subtitle: 'Як ми обробляємо ваші дані',
    lastUpdated: '12 березня 2026',
    sections: [
      {
        title: '1. Адміністратор даних',
        content: `Адміністратором ваших персональних даних є ${COMPANY.name}, ${COMPANY.address}, NIP: ${COMPANY.nip}, REGON: ${COMPANY.regon}.

Контакт: ${COMPANY.email}`,
      },
      {
        title: '2. Які дані ми збираємо',
        content: `Ми збираємо такі категорії даних:

• Ідентифікаційні дані (ім'я, прізвище, податковий номер)
• Контактні дані (електронна адреса)
• Фінансові дані (доходи, витрати - виключно для податкових розрахунків)
• Технічні дані (IP-адреса, тип браузера, логи доступу)

Усі фінансові дані обробляються локально у вашому браузері або надійно шифруються.`,
      },
      {
        title: '3. Цілі обробки',
        content: `Ми обробляємо ваші дані для:

• Надання послуг податкових розрахунків (ст. 6(1)(b) GDPR)
• Комунікації з вами (ст. 6(1)(b) GDPR)
• Виконання юридичних зобов'язань (ст. 6(1)(c) GDPR)
• Покращення наших послуг (ст. 6(1)(f) GDPR)`,
      },
      {
        title: '4. Термін зберігання',
        content: `Ми зберігаємо дані:

• Дані облікового запису - протягом періоду користування послугою
• Фінансові дані - період, вимаганий податковим законодавством (5 років)
• Технічні логи - максимум 12 місяців
• Аналітичні дані та записи сесій (PostHog, після згоди) - планований максимум 12 місяців після впровадження налаштування ретенції в PostHog`,
      },
      {
        title: '5. Ваші права',
        content: `Ви маєте право на:

• Доступ до своїх даних
• Виправлення даних
• Видалення даних ("право бути забутим")
• Обмеження обробки
• Перенесення даних
• Заперечення проти обробки
• Відкликання згоди в будь-який час

Щоб скористатися цими правами, зв'яжіться з нами: ${COMPANY.email}

Запити на видалення ми також передаємо нашим обробникам, якщо вони зберігають ідентифікатори або допоміжні записи, пов'язані з вашим обліковим записом.`,
      },
      {
        title: '6. Обробники даних і передача даних',
        content: `Ми користуємося постачальниками, які обробляють дані від нашого імені лише в обсязі, необхідному для надання послуги:

• PostHog Inc. - аналітика продукту та записи сесій після надання згоди; дані EU Cloud розміщуються в регіоні EU (Frankfurt / Germany); стандартна DPA: https://posthog.com/dpa; список субпроцесорів: https://posthog.com/subprocessors
• Vercel Inc. - хостинг і доставка додатку
• Supabase Inc. - інфраструктура бази даних, автентифікація користувачів і зберігання даних облікового запису
• Resend, Inc. - надсилання транзакційних листів і комунікація підтримки
• Stripe, Inc. - обробка платежів, якщо ви користуєтеся платними функціями

Ми передаємо лише ті дані, які необхідні для відповідної послуги.`,
      },
      {
        title: '7. Cookies, згода та записи сесій',
        content: `На публічних сторінках ми зберігаємо cookie згоди \`bookeper_consent\`, щоб запам'ятати ваш вибір щодо аналітики. У додатку ми також зберігаємо \`bookeper_cookie_consent\` у пам'яті браузера, щоб відновити вибір користувача після входу.

Після надання згоди в додатку Bookeper ми вмикаємо продуктову аналітику PostHog разом із first-party cookies і записами localStorage, необхідними для прив'язки подій до сесії користувача та вимірювання використання функцій.

Записи сесій у PostHog використовуються лише для аналізу якості продукту, налагодження помилок і розуміння шляху користувача. Поля форм і вибрані ділянки інтерфейсу маскуються, а шаблони ідентифікаційних даних ми редагуємо до відправлення в аналітичний інструмент.

Ви можете відкликати згоду в будь-який момент через налаштування браузера. У додатку ми також надаємо окрему кнопку на сторінці політики конфіденційності, яка знову відкриває налаштування cookie.`,
      },
      {
        title: '8. Безпека даних',
        content: `Ми застосовуємо відповідні технічні та організаційні заходи:

• Шифрування даних (TLS/SSL)
• Регулярні резервні копії
• Контроль доступу
• Моніторинг безпеки

Наші калькулятори працюють детерміновано - ми не використовуємо ШІ для обробки ваших фінансових даних.`,
      },
    ],
  },
};

export const termsOfService: MultilingualLegalDocument = {
  pl: {
    title: 'Regulamin',
    subtitle: 'Warunki korzystania z usługi',
    lastUpdated: '21 grudnia 2024',
    sections: [
      {
        title: '1. Postanowienia ogólne',
        content: `Niniejszy regulamin określa zasady korzystania z serwisu ${COMPANY.tradingName} dostępnego pod adresem ${COMPANY.website}.

Usługodawcą jest ${COMPANY.name}, ${COMPANY.address}, NIP: ${COMPANY.nip}, REGON: ${COMPANY.regon}.`,
      },
      {
        title: '2. Definicje',
        content: `• Usługa - aplikacja ${COMPANY.tradingName} do kalkulacji podatkowych dla JDG
• Użytkownik - osoba korzystająca z Usługi
• Konto - indywidualne konto Użytkownika w systemie
• Subskrypcja - płatny dostęp do funkcji premium`,
      },
      {
        title: '3. Zakres usługi',
        content: `${COMPANY.tradingName} oferuje:

• Kalkulatory podatkowe (ZUS DRA, PIT-28, JPK_V7M)
• Dostęp do bazy interpretacji KIS
• Automatyczne przypomnienia o terminach
• Wsparcie w rozliczeniach podatkowych

Kalkulatory działają deterministycznie na podstawie aktualnych przepisów. Nie udzielamy porad prawnych ani podatkowych.

Bookeper przygotowuje projekty dokumentów i deklaracji, które Użytkownik sam sprawdza, podpisuje i składa. Bookeper nie zastępuje doradcy podatkowego w sprawach wymagających indywidualnej porady prawno-podatkowej.`,
      },
      {
        title: '4. Odpowiedzialność',
        content: `Usługa ma charakter informacyjny i pomocniczy. Nie zastępuje profesjonalnej porady księgowej lub podatkowej.

Użytkownik ponosi odpowiedzialność za:
• Prawidłowość wprowadzonych danych
• Terminowe składanie deklaracji
• Weryfikację wyników kalkulacji

Usługodawca nie ponosi odpowiedzialności za szkody wynikające z błędów w danych wprowadzonych przez Użytkownika.`,
      },
      {
        title: '5. Płatności i subskrypcja',
        content: `Cena subskrypcji wynosi 50 PLN miesięcznie.

• Płatność pobierana jest z góry za każdy miesiąc
• Możliwość anulowania w dowolnym momencie
• Brak zwrotów za niewykorzystany okres
• Faktury VAT wystawiane automatycznie`,
      },
      {
        title: '6. Reklamacje',
        content: `Reklamacje można składać:

• Mailowo: ${COMPANY.email}
• Pisemnie na adres siedziby

Termin rozpatrzenia reklamacji: 14 dni roboczych.`,
      },
      {
        title: '7. Zmiany regulaminu',
        content: `Usługodawca zastrzega prawo do zmiany regulaminu. O zmianach Użytkownicy zostaną powiadomieni z 14-dniowym wyprzedzeniem.

Kontynuowanie korzystania z usługi po wejściu zmian w życie oznacza ich akceptację.`,
      },
    ],
  },
  en: {
    title: 'Terms of Service',
    subtitle: 'Terms and conditions of use',
    lastUpdated: 'December 21, 2024',
    sections: [
      {
        title: '1. General Provisions',
        content: `These terms govern the use of ${COMPANY.tradingName} service available at ${COMPANY.website}.

The service provider is ${COMPANY.name}, ${COMPANY.address}, NIP: ${COMPANY.nip}, REGON: ${COMPANY.regon}.`,
      },
      {
        title: '2. Definitions',
        content: `• Service - ${COMPANY.tradingName} tax calculation application for Polish freelancers
• User - person using the Service
• Account - individual User account in the system
• Subscription - paid access to premium features`,
      },
      {
        title: '3. Scope of Service',
        content: `${COMPANY.tradingName} offers:

• Tax calculators (ZUS DRA, PIT-28, JPK_V7M)
• Access to KIS interpretations database
• Automatic deadline reminders
• Tax settlement support

Calculators work deterministically based on current regulations. We do not provide legal or tax advice.

Bookeper prepares document and declaration drafts that the User reviews, signs, and files themselves. Bookeper does not replace a licensed tax advisor for matters requiring individual tax-law advice.`,
      },
      {
        title: '4. Liability',
        content: `The service is informational and auxiliary. It does not replace professional accounting or tax advice.

The User is responsible for:
• Accuracy of entered data
• Timely filing of declarations
• Verification of calculation results

The Provider is not liable for damages resulting from errors in User-entered data.`,
      },
      {
        title: '5. Payments and Subscription',
        content: `Subscription price is 50 PLN per month.

• Payment is charged in advance for each month
• Can be cancelled at any time
• No refunds for unused period
• VAT invoices issued automatically`,
      },
      {
        title: '6. Complaints',
        content: `Complaints can be submitted:

• By email: ${COMPANY.email}
• In writing to the registered address

Complaint processing time: 14 business days.`,
      },
      {
        title: '7. Changes to Terms',
        content: `The Provider reserves the right to change these terms. Users will be notified 14 days in advance.

Continued use of the service after changes take effect constitutes acceptance.`,
      },
    ],
  },
  uk: {
    title: 'Умови використання',
    subtitle: 'Умови користування послугою',
    lastUpdated: '21 грудня 2024',
    sections: [
      {
        title: '1. Загальні положення',
        content: `Ці умови регулюють використання сервісу ${COMPANY.tradingName}, доступного за адресою ${COMPANY.website}.

Постачальником послуг є ${COMPANY.name}, ${COMPANY.address}, NIP: ${COMPANY.nip}, REGON: ${COMPANY.regon}.`,
      },
      {
        title: '2. Визначення',
        content: `• Послуга - додаток ${COMPANY.tradingName} для податкових розрахунків для польських фрілансерів
• Користувач - особа, яка використовує Послугу
• Обліковий запис - індивідуальний обліковий запис Користувача в системі
• Підписка - платний доступ до преміум-функцій`,
      },
      {
        title: '3. Обсяг послуги',
        content: `${COMPANY.tradingName} пропонує:

• Податкові калькулятори (ZUS DRA, PIT-28, JPK_V7M)
• Доступ до бази інтерпретацій KIS
• Автоматичні нагадування про терміни
• Підтримка в податкових розрахунках

Калькулятори працюють детерміновано на основі чинного законодавства. Ми не надаємо юридичних або податкових консультацій.

Bookeper готує проєкти документів і декларацій, які Користувач самостійно перевіряє, підписує та подає. Bookeper не замінює податкового консультанта у справах, що потребують індивідуальної податково-правової консультації.`,
      },
      {
        title: '4. Відповідальність',
        content: `Послуга має інформаційний та допоміжний характер. Вона не замінює професійну бухгалтерську або податкову консультацію.

Користувач несе відповідальність за:
• Правильність введених даних
• Своєчасне подання декларацій
• Перевірку результатів розрахунків

Постачальник не несе відповідальності за збитки, що виникли через помилки в даних, введених Користувачем.`,
      },
      {
        title: '5. Платежі та підписка',
        content: `Вартість підписки становить 50 PLN на місяць.

• Оплата стягується авансом за кожен місяць
• Можна скасувати в будь-який час
• Повернення за невикористаний період не передбачено
• Рахунки-фактури ПДВ видаються автоматично`,
      },
      {
        title: '6. Скарги',
        content: `Скарги можна подавати:

• Електронною поштою: ${COMPANY.email}
• Письмово на юридичну адресу

Термін розгляду скарги: 14 робочих днів.`,
      },
      {
        title: '7. Зміни умов',
        content: `Постачальник залишає за собою право змінювати ці умови. Користувачі будуть повідомлені за 14 днів.

Продовження використання послуги після набрання змін чинності означає їх прийняття.`,
      },
    ],
  },
};

export const cookiePolicy: MultilingualLegalDocument = {
  pl: {
    title: 'Polityka Cookies',
    subtitle: 'Informacje o plikach cookie',
    lastUpdated: '12 marca 2026',
    sections: [
      {
        title: '1. Czym są pliki cookie',
        content: `Pliki cookie (ciasteczka) to małe pliki tekstowe zapisywane na Twoim urządzeniu podczas korzystania ze strony internetowej.

Używamy ich do prawidłowego działania serwisu i poprawy Twojego doświadczenia.`,
      },
      {
        title: '2. Jakich cookies używamy',
        content: `Cookies i podobne technologie wykorzystywane na stronach publicznych oraz w aplikacji:

Niezbędne cookies:
• bookeper_consent - zapamiętanie Twojej zgody na cookies
• session_id - utrzymanie sesji logowania

Analityczne cookies i wpisy pamięci przeglądarki (tylko za Twoją zgodą):
• Vercel Analytics - anonimowe statystyki odwiedzin stron publicznych
• bookeper_cookie_consent (localStorage) - odtworzenie decyzji analitycznej po zalogowaniu
• PostHog - first-party cookies i wpisy localStorage potrzebne do analityki produktu, przypisania zdarzeń do sesji użytkownika i pomiaru wykorzystania funkcji w aplikacji

Nagrania sesji w PostHog uruchamiamy wyłącznie po wyrażeniu zgody. Pola formularzy oraz wskazane obszary interfejsu są maskowane przed zapisaniem nagrania.

Nie używamy cookies reklamowych ani śledzących.`,
      },
      {
        title: '3. Jak długo przechowujemy cookies',
        content: `• Cookies sesyjne - usuwane po zamknięciu przeglądarki
• Cookies i wpisy zgody (\`bookeper_consent\`, \`bookeper_cookie_consent\`) - do 12 miesięcy lub do czasu ich usunięcia
• Vercel Analytics - zgodnie z polityką dostawcy
• Identyfikatory analityczne PostHog i powiązane nagrania sesji - planowany maksymalnie 12-miesięczny okres retencji po wdrożeniu ustawienia w PostHog`,
      },
      {
        title: '4. Jak zarządzać cookies',
        content: `Możesz zarządzać cookies poprzez:

• Banner zgody na naszej stronie
• Ustawienia przeglądarki

Większość przeglądarek pozwala:
• Blokować wszystkie cookies
• Akceptować tylko wybrane cookies
• Usuwać cookies po każdej sesji

Wyłączenie niezbędnych cookies może uniemożliwić korzystanie z niektórych funkcji serwisu.`,
      },
      {
        title: '5. Cookies stron trzecich',
        content: `Korzystamy z następujących usług zewnętrznych:

• Vercel Analytics - anonimowe statystyki odwiedzin stron publicznych
• PostHog Inc. - analityka produktu i nagrania sesji po wyrażeniu zgody; dane EU Cloud są hostowane w regionie EU (Frankfurt / Niemcy); standardowa DPA: https://posthog.com/dpa; lista subprocesorów: https://posthog.com/subprocessors
• Stripe - płatności (tylko na stronie płatności)

Każda z tych usług ma własną politykę prywatności.`,
      },
      {
        title: '6. Kontakt',
        content: `W sprawie cookies i prywatności skontaktuj się z nami:

${COMPANY.email}

${COMPANY.name}
${COMPANY.address}`,
      },
    ],
  },
  en: {
    title: 'Cookie Policy',
    subtitle: 'Information about cookies',
    lastUpdated: 'March 12, 2026',
    sections: [
      {
        title: '1. What are cookies',
        content: `Cookies are small text files stored on your device when using a website.

We use them for proper site functionality and to improve your experience.`,
      },
      {
        title: '2. Cookies we use',
        content: `Cookies and similar browser technologies used on the public site and inside the application:

Essential cookies:
• bookeper_consent - remembering your cookie consent
• session_id - maintaining login session

Analytics cookies and browser-storage entries (only with your consent):
• Vercel Analytics - anonymous traffic statistics for public pages
• bookeper_cookie_consent (localStorage) - restoring your analytics decision after sign-in
• PostHog - first-party cookies and localStorage entries required for product analytics, associating events with a user session, and measuring feature usage inside the application

PostHog session recordings start only after consent is granted. Form fields and selected interface regions are masked before a recording is stored.

We do not use advertising or tracking cookies.`,
      },
      {
        title: '3. How long we store cookies',
        content: `• Session cookies - deleted when browser closes
• Consent cookies and storage entries (\`bookeper_consent\`, \`bookeper_cookie_consent\`) - up to 12 months or until deleted
• Vercel Analytics - according to the provider policy
• PostHog analytics identifiers and related session recordings - planned maximum 12-month retention once the PostHog setting is applied`,
      },
      {
        title: '4. Managing cookies',
        content: `You can manage cookies through:

• Consent banner on our site
• Browser settings

Most browsers allow you to:
• Block all cookies
• Accept only selected cookies
• Delete cookies after each session

Disabling essential cookies may prevent some site features from working.`,
      },
      {
        title: '5. Third-party cookies',
        content: `We use the following external services:

• Vercel Analytics - anonymous analytics for public-site traffic
• PostHog Inc. - product analytics and session recordings after consent; EU Cloud data is hosted in the EU region (Frankfurt / Germany); standard DPA: https://posthog.com/dpa; subprocessors list: https://posthog.com/subprocessors
• Stripe - payments (only on payment page)

Each of these services has its own privacy policy.`,
      },
      {
        title: '6. Contact',
        content: `For cookie and privacy inquiries, contact us:

${COMPANY.email}

${COMPANY.name}
${COMPANY.address}`,
      },
    ],
  },
  uk: {
    title: 'Політика файлів cookie',
    subtitle: 'Інформація про файли cookie',
    lastUpdated: '12 березня 2026',
    sections: [
      {
        title: '1. Що таке файли cookie',
        content: `Файли cookie - це невеликі текстові файли, що зберігаються на вашому пристрої під час використання веб-сайту.

Ми використовуємо їх для належного функціонування сайту та покращення вашого досвіду.`,
      },
      {
        title: '2. Які cookie ми використовуємо',
        content: `Cookie та подібні технології браузера, які ми використовуємо на публічних сторінках і в додатку:

Необхідні cookie:
• bookeper_consent - запам'ятовування вашої згоди на cookie
• session_id - підтримка сесії входу

Аналітичні cookie та записи у пам'яті браузера (лише за вашою згодою):
• Vercel Analytics - анонімна статистика відвідувань публічних сторінок
• bookeper_cookie_consent (localStorage) - відновлення вашого рішення щодо аналітики після входу
• PostHog - first-party cookies і записи localStorage, потрібні для продуктової аналітики, прив'язки подій до сесії користувача та вимірювання використання функцій у додатку

Записи сесій у PostHog запускаються лише після надання згоди. Поля форм і вибрані ділянки інтерфейсу маскуються до збереження запису.

Ми не використовуємо рекламні або відстежувальні cookie.`,
      },
      {
        title: '3. Як довго ми зберігаємо cookie',
        content: `• Сесійні cookie - видаляються при закритті браузера
• Cookie та записи згоди (\`bookeper_consent\`, \`bookeper_cookie_consent\`) - до 12 місяців або до їх видалення
• Vercel Analytics - згідно з політикою постачальника
• Аналітичні ідентифікатори PostHog і пов'язані записи сесій - планований максимум 12 місяців після впровадження налаштування ретенції в PostHog`,
      },
      {
        title: '4. Керування cookie',
        content: `Ви можете керувати cookie через:

• Банер згоди на нашому сайті
• Налаштування браузера

Більшість браузерів дозволяють:
• Блокувати всі cookie
• Приймати лише вибрані cookie
• Видаляти cookie після кожної сесії

Вимкнення необхідних cookie може унеможливити роботу деяких функцій сайту.`,
      },
      {
        title: '5. Cookie третіх сторін',
        content: `Ми використовуємо такі зовнішні сервіси:

• Vercel Analytics - анонімна аналітика трафіку публічних сторінок
• PostHog Inc. - аналітика продукту та записи сесій після надання згоди; дані EU Cloud розміщуються в регіоні EU (Frankfurt / Germany); стандартна DPA: https://posthog.com/dpa; список субпроцесорів: https://posthog.com/subprocessors
• Stripe - платежі (лише на сторінці оплати)

Кожен з цих сервісів має власну політику конфіденційності.`,
      },
      {
        title: '6. Контакт',
        content: `З питань cookie та конфіденційності зв'яжіться з нами:

${COMPANY.email}

${COMPANY.name}
${COMPANY.address}`,
      },
    ],
  },
};
